import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type Highcharts from "highcharts";
import styled from "styled-components";

import { Table, Map } from "@similarweb/pro-logic-components";

import { useMapGeoJson } from "app/common/hooks";
import { countryByIdFilter } from "app/filters/ngFilters";

import { FIRST_PAGE } from "../utils/utils";

import {
    type CountryData,
    getColumnConfig,
    getMapConfig,
    type getMapDimensionsByWindowWidth,
    PAGE_SIZE,
    PREVIEW_ITEM_COUNT,
    transformCountryDataToMapPoints,
} from "./configs/CountryMapWidgetConfigs";
import type { ICountriesResponse } from "./hooks/useCountries";

const CountryMapAndTableGrid = styled.div<{ mapDimensions: ReturnType<typeof getMapDimensionsByWindowWidth> }>`
    display: grid;
    grid-template-columns: ${({ mapDimensions }) => mapDimensions.width}px minmax(0, 1fr);
    gap: 24px;
    margin: 24px;
`;

export interface ICountryMapAndTableProps {
    data?: ICountriesResponse;
    mapDimensions: ReturnType<typeof getMapDimensionsByWindowWidth>;
    shouldLimitData?: boolean;
    resetKey?: string;
}

export const CountryMapAndTable = ({
    data,
    mapDimensions,
    shouldLimitData = false,
    resetKey,
}: ICountryMapAndTableProps) => {
    const { data: mapGeoJson, isLoading: isMapGeoJsonLoading } = useMapGeoJson();
    const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
    const chartRef = useRef<Highcharts.Chart | null>(null);
    const getCountryById = countryByIdFilter();

    useEffect(() => {
        setCurrentPage(FIRST_PAGE);
    }, [resetKey]);

    const countryData = useMemo(() => {
        const allData = data?.countrySpendShares ?? [];
        return shouldLimitData ? allData.slice(0, PREVIEW_ITEM_COUNT) : allData;
    }, [data, shouldLimitData]);

    const mapData = useMemo(() => {
        if (countryData.length === 0) {
            return [];
        }
        return transformCountryDataToMapPoints(countryData);
    }, [countryData]);

    const chartConfig = useMemo(() => getMapConfig(mapDimensions), [mapDimensions]);

    const handlePageChange = (_pageSize: number, _total: number, page: number) => {
        setCurrentPage(page);
    };

    const handleChartCreated = useCallback((chart: Highcharts.Chart) => {
        chartRef.current = chart;
    }, []);

    const handleRowMouseEnter = useCallback(
        (record: CountryData) => {
            const countryObj = getCountryById(record.country);
            if (countryObj?.code && chartRef.current && chartRef.current.series[0]) {
                const series = chartRef.current.series[0];
                const point = series.data.find((p: Highcharts.Point) => p["hc-key"] === countryObj.code);
                if (point) {
                    point.setState("hover");
                }
            }
        },
        [getCountryById],
    );

    const handleRowMouseLeave = useCallback(() => {
        if (chartRef.current && chartRef.current.series[0]) {
            const series = chartRef.current.series[0];
            series.data.forEach((point: Highcharts.Point) => {
                point.setState("");
            });
        }
    }, []);

    return (
        <CountryMapAndTableGrid mapDimensions={mapDimensions}>
            <Map
                config={chartConfig}
                mapData={mapGeoJson}
                data={mapData}
                isLoading={isMapGeoJsonLoading}
                style={{
                    height: "100%",
                    width: mapDimensions.width,
                    display: "flex",
                    alignItems: "center",
                }}
                getRef={handleChartCreated}
            />
            <Table
                key={mapDimensions.width.toString()}
                theme="white"
                data={countryData}
                columnConfig={getColumnConfig(currentPage, !shouldLimitData)}
                bordersConfig={{ body: false, header: false }}
                pagination={
                    shouldLimitData
                        ? undefined
                        : {
                              pageSize: PAGE_SIZE,
                              current: currentPage,
                              onChange: handlePageChange,
                          }
                }
                onRow={(record) => ({
                    onMouseEnter: () => handleRowMouseEnter(record),
                    onMouseLeave: handleRowMouseLeave,
                })}
            />
        </CountryMapAndTableGrid>
    );
};
